import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import HomeNav from './HomeNav'
import Footer from './Footer'

const BRAND = '#6eb446'

// ── Shared styles ─────────────────────────────────────────────────────────────
const bodyText: React.CSSProperties = {
  margin: 0,
  fontSize: '0.9rem',
  color: '#475569',
  lineHeight: 1.8,
}

const subHeading: React.CSSProperties = {
  margin: '0 0 0.5rem',
  fontSize: '0.92rem',
  fontWeight: 700,
  color: '#1e293b',
}

// ── Section (top-level numbered) ──────────────────────────────────────────────
interface SectionProps {
  num: string
  title: string
  children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({ num, title, children }) => (
  <div style={{
    display: 'flex',
    gap: '1.25rem',
    paddingBottom: '2rem',
    borderBottom: '1px solid #f1f5f9',
  }}>
    <span style={{
      flexShrink: 0,
      width: 34,
      height: 34,
      borderRadius: '50%',
      backgroundColor: '#f4fbf0',
      border: '1.5px solid #c8edb0',
      color: BRAND,
      fontWeight: 800,
      fontSize: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 2,
    }}>
      {num}
    </span>
    <div style={{ flex: 1 }}>
      <h2 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
        {title}
      </h2>
      {children}
    </div>
  </div>
)

// ── SubSection (indented) ─────────────────────────────────────────────────────
interface SubSectionProps {
  title: string
  children: React.ReactNode
}

const SubSection: React.FC<SubSectionProps> = ({ title, children }) => (
  <div style={{
    marginTop: '1rem',
    paddingLeft: '1rem',
    borderLeft: `2px solid #e8f5dc`,
  }}>
    <p style={subHeading}>{title}</p>
    {children}
  </div>
)

// ── BulletList ────────────────────────────────────────────────────────────────
const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
    {items.map((item, i) => (
      <li key={i} style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.7 }}>{item}</li>
    ))}
  </ul>
)

// ── TermsAndConditions ────────────────────────────────────────────────────────
const TermsAndConditions: React.FC = () => {
  const { t } = useTranslation()

  useEffect(() => {
    document.body.classList.add('page-home')
    return () => document.body.classList.remove('page-home')
  }, [])

  return (
    <>
      <HomeNav />

      {/* Hero */}
      <section style={{ backgroundColor: '#f8faf6', borderBottom: '4px solid #000', padding: '4rem 1.5rem 3rem' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <span style={{
            display: 'inline-block',
            padding: '4px 16px',
            borderRadius: 9999,
            backgroundColor: '#e8f5dc',
            color: BRAND,
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '1rem',
          }}>
            •
          </span>
          <h1 style={{
            margin: 0,
            fontSize: 'clamp(1.8rem, 5vw, 2.75rem)',
            fontWeight: 900,
            color: '#1e293b',
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
          }}>
            {t('terms_and_conditions.main_title')}
            <span style={{ color: BRAND }}> Portal Label Dictate</span>
          </h1>
        </div>
      </section>

      {/* Content */}
      <main style={{ backgroundColor: '#fff', padding: '3rem 1.5rem 5rem' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '2rem' }}>

          {/* 1 */}
          <Section num="1" title={t('terms_and_conditions.title1')}>
            <p style={bodyText}>{t('terms_and_conditions.text1')}</p>
          </Section>

          {/* 2 */}
          <Section num="2" title={t('terms_and_conditions.title2')}>
            <p style={bodyText}>{t('terms_and_conditions.text2')}</p>
          </Section>

          {/* 3 */}
          <Section num="3" title={t('terms_and_conditions.title3')}>
            <p style={bodyText}>{t('terms_and_conditions.text3')}</p>

            <SubSection title={t('terms_and_conditions.title3_1')}>
              {[
                t('terms_and_conditions.text3_1'),
                t('terms_and_conditions.text3_2'),
                t('terms_and_conditions.text3_3'),
                t('terms_and_conditions.text3_4'),
                t('terms_and_conditions.text3_5'),
              ].map((text, i) => (
                <p key={i} style={{ ...bodyText, marginBottom: i < 4 ? '0.5rem' : 0 }}>{text}</p>
              ))}
            </SubSection>

            <SubSection title={t('terms_and_conditions.title3_2')}>
              <p style={{ ...bodyText, marginBottom: '0.5rem' }}>{t('terms_and_conditions.text3_6')}</p>
              <BulletList items={[
                t('terms_and_conditions.text3_7'),
                t('terms_and_conditions.text3_7_1'),
                t('terms_and_conditions.text3_7_2'),
                t('terms_and_conditions.text3_7_3'),
                t('terms_and_conditions.text3_7_4'),
              ]} />
              {[
                t('terms_and_conditions.text3_8'),
                t('terms_and_conditions.text3_9'),
                t('terms_and_conditions.text3_10'),
                t('terms_and_conditions.text3_11'),
                t('terms_and_conditions.text3_12'),
                t('terms_and_conditions.text3_13'),
              ].map((text, i) => (
                <p key={i} style={{ ...bodyText, marginTop: '0.5rem' }}>{text}</p>
              ))}
            </SubSection>
          </Section>

          {/* 4 */}
          <Section num="4" title={t('terms_and_conditions.title4')}>
            <p style={bodyText}>
              {t('terms_and_conditions.text4')}{' '}
              <Link to="/privacy-policy" style={{ color: BRAND, fontWeight: 600 }}>
                Privacy Policy
              </Link>
              {', '}
              {t('terms_and_conditions.text4_1')}
            </p>
          </Section>

          {/* 5 */}
          <Section num="5" title={t('terms_and_conditions.title5')}>
            <p style={bodyText}>{t('terms_and_conditions.text5')}</p>

            <SubSection title={t('terms_and_conditions.title5_1')}>
              <p style={{ ...bodyText, marginBottom: '0.5rem' }}>{t('terms_and_conditions.text5_1')}</p>
              <p style={bodyText}>{t('terms_and_conditions.text5_1_2')}</p>
            </SubSection>

            <SubSection title={t('terms_and_conditions.title5_2')}>
              <p style={{ ...bodyText, marginBottom: '0.5rem' }}>{t('terms_and_conditions.text5_2_1')}</p>
              <p style={{ ...bodyText, marginBottom: '0.5rem' }}>{t('terms_and_conditions.text5_2_2')}</p>
              <p style={bodyText}>{t('terms_and_conditions.text5_2_3')}</p>

              <div style={{ marginTop: '0.75rem', paddingLeft: '1rem', borderLeft: '2px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <p style={subHeading}>{t('terms_and_conditions.title5_2_1')}</p>
                  {[
                    t('terms_and_conditions.text5_2_1_1'),
                    t('terms_and_conditions.text5_2_1_2'),
                    t('terms_and_conditions.text5_2_1_3'),
                    t('terms_and_conditions.text5_2_1_4'),
                    t('terms_and_conditions.text5_2_1_5'),
                    t('terms_and_conditions.text5_2_1_6'),
                  ].map((text, i) => (
                    <p key={i} style={{ ...bodyText, marginTop: i > 0 ? '0.4rem' : 0 }}>{text}</p>
                  ))}
                </div>
                <div>
                  <p style={subHeading}>{t('terms_and_conditions.title5_2_2')}</p>
                  {[
                    t('terms_and_conditions.text5_2_2_1'),
                    t('terms_and_conditions.text5_2_2_2'),
                    t('terms_and_conditions.text5_2_2_3'),
                    t('terms_and_conditions.text5_2_2_4'),
                    t('terms_and_conditions.text5_2_2_5'),
                    t('terms_and_conditions.text5_2_2_6'),
                  ].map((text, i) => (
                    <p key={i} style={{ ...bodyText, marginTop: i > 0 ? '0.4rem' : 0 }}>{text}</p>
                  ))}
                </div>
              </div>
            </SubSection>

            <SubSection title={t('terms_and_conditions.title5_3')}>
              <p style={bodyText}>{t('terms_and_conditions.text5_3')}</p>
              <div style={{ marginTop: '0.75rem', paddingLeft: '1rem', borderLeft: '2px solid #f1f5f9' }}>
                <p style={subHeading}>{t('terms_and_conditions.title5_3_1')}</p>
                {[
                  t('terms_and_conditions.text5_3_1'),
                  t('terms_and_conditions.text5_3_2'),
                  t('terms_and_conditions.text5_3_3'),
                  t('terms_and_conditions.text5_3_4'),
                  t('terms_and_conditions.text5_3_5'),
                  t('terms_and_conditions.text5_3_6'),
                ].map((text, i) => (
                  <p key={i} style={{ ...bodyText, marginTop: i > 0 ? '0.4rem' : 0 }}>{text}</p>
                ))}
              </div>
            </SubSection>

            <SubSection title={t('terms_and_conditions.title5_4')}>
              <p style={bodyText}>{t('terms_and_conditions.text5_4')}</p>
            </SubSection>

            <SubSection title={t('terms_and_conditions.title5_5')}>
              <p style={{ ...bodyText, marginBottom: '0.5rem' }}>{t('terms_and_conditions.text5_5')}</p>
              <p style={bodyText}>{t('terms_and_conditions.text5_5_1')}</p>
            </SubSection>

            <SubSection title={t('terms_and_conditions.title5_6')}>
              {[
                t('terms_and_conditions.text5_6'),
                t('terms_and_conditions.text5_6_1'),
                t('terms_and_conditions.text5_6_2'),
                t('terms_and_conditions.text5_6_3'),
              ].map((text, i) => (
                <p key={i} style={{ ...bodyText, marginTop: i > 0 ? '0.5rem' : 0 }}>{text}</p>
              ))}
            </SubSection>
          </Section>

          {/* 6 */}
          <Section num="6" title={t('terms_and_conditions.title6')}>
            <p style={bodyText}>{t('terms_and_conditions.text6')}</p>
          </Section>

          {/* 7 */}
          <Section num="7" title={t('terms_and_conditions.title7')}>
            <p style={bodyText}>{t('terms_and_conditions.text7')}</p>
          </Section>

          {/* 8 */}
          <Section num="8" title={t('terms_and_conditions.title8')}>
            <p style={bodyText}>{t('terms_and_conditions.text8')}</p>
          </Section>

          {/* 9 */}
          <Section num="9" title={t('terms_and_conditions.title9')}>
            <p style={bodyText}>{t('terms_and_conditions.text9')}</p>
          </Section>

        </div>
      </main>

      <Footer />
    </>
  )
}

export default TermsAndConditions
